/**
 * THE one string every visual channel of the overdue hire signal feeds
 * from — colour, `title`, `sr-only`. Renders the owner's ruling (spec
 * 2026-09-01, `docs/superpowers/specs/2026-09-01-off-hire-dialog-design.md`)
 * on list surfaces: an off-hire date is evidence about PAPERWORK, not
 * money, so the price NEVER moves on its own. Instead the row WARNS —
 * "this hire is overdue and the estimated extra cost is £X" — and the
 * estimate is never persisted, never applied.
 *
 * Consumes `item.hire_status`, `item.overrun_days`,
 * `item.overdue_extra_cost` (server-computed, `hireOverrunDelta` in
 * `server/helpers/hireCost.js`) and `item.hire_rate_type`.
 *
 * 🚨 THE TRAP: `overdue_extra_cost` is a SIGNED whole-span delta measured to
 * `actual_return_date || today` and is NEGATIVE for every pending/on-hire
 * row (a hire starting in 2099 reads negative today). The gate that avoids
 * rendering that as invented "savings" is `item.hire_status === 'overdue'`
 * — the server's own status field, independent of the raw delta arithmetic
 * — NOT a sign check on the cost. Confirmed against `server/routes/hires.js`:
 * `overrunDays` (and hence any genuine positive `overdue_extra_cost`) is
 * only ever computed while `hire_status === 'overdue'` or the item has just
 * been returned late; for every other status it is 0/negative by
 * construction. See this file's test for the mutation this pins.
 *
 * 🪤 `0` is a REAL answer (an overdue hire still inside its already-paid
 * week genuinely costs £0 extra — `Number.isFinite`, never a bare falsy
 * test) and `null`/non-finite means UNKNOWABLE, rendered with no £ figure
 * at all rather than a guess.
 */
export function hireOverdueSignalLabel(item, formatMoney) {
  if (!item || item.hire_status !== 'overdue') return null;

  const days = Number(item.overrun_days);
  if (!Number.isFinite(days) || days <= 0) return null;

  const base = `Overdue by ${days} ${days === 1 ? 'day' : 'days'}`;
  const cost = item.overdue_extra_cost;

  if (Number.isFinite(cost) && cost > 0) {
    return `${base} — estimated extra cost ${formatMoney(cost)}. Not charged automatically.`;
  }

  if (Number.isFinite(cost) && cost === 0 && item.hire_rate_type === 'weekly') {
    return `${base} — within the week already paid for, no extra cost expected.`;
  }

  // Flat-rate £0, unknowable (`null`/non-finite), and — defensively, though
  // it should not occur while genuinely overdue — a negative delta all fall
  // through here: the plain sentence, never a figure we cannot stand behind.
  return `${base}.`;
}
